import { Pie } from '@ant-design/charts';
import { VizState, useCubeQuery } from '@cubejs-client/react';
import { useGet } from '@threatadvisor/react-utils';
import { PieChartData } from '@threatadvisor/react-utils/src/types/generated.SecurityEvent';
import { Col, Row } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

export type PercentagesProps = {
  securityEventApi: string;
  clientUid: string;
  reportStart: string;
  reportEnd: string;
};

export default function Percentages(props: PercentagesProps): ReactElement {
  const { securityEventApi, clientUid, reportStart, reportEnd } = props;

  //event queries

  const eventsStatusQuery: VizState = {
    chartType: 'table',
    query: {
      measures: [
        'security_event.count',
        'security_event_extended.cleared_count',
        'security_event_extended.linked_count',
        'security_event_extended.escalated_count'
      ],
      dimensions: ['security_event.status'],
      order: {},
      filters: [
        {
          member: 'security_event.createdDate',
          operator: 'inDateRange',
          values: [reportStart, reportEnd]
        },
        {
          member: 'security_event.clientUid',
          operator: 'equals',
          values: [clientUid]
        },
        {
          member: 'security_event.priority',
          operator: 'notEquals',
          values: ['P5', '5']
        }
      ]
    }
  };

  const eventsOverSLAQuery: VizState = {
    chartType: 'number',
    pivotConfig: {
      x: [],
      y: ['measures']
    },
    query: {
      measures: ['security_event.count'],
      order: {},
      filters: [
        {
          member: 'security_event.clientUid',
          operator: 'equals',
          values: [clientUid]
        },
        {
          member: 'security_event.priority',
          operator: 'notEquals',
          values: ['P5', '5']
        },
        {
          member: 'security_event.createdDate',
          operator: 'inDateRange',
          values: [reportStart, reportEnd]
        },
        {
          member: 'security_event_extended.time_to_action_minutes',
          operator: 'gte',
          values: ['30']
        }
      ]
    }
  };
  //end event queries

  //detection queries
  const investigationsClosedQuery: VizState = {
    chartType: 'number',
    pivotConfig: {
      x: [],
      y: ['measures']
    },
    query: {
      measures: ['detection_case.count'],
      timeDimensions: [
        {
          dimension: 'detection_case.closedDate',
          dateRange: [reportStart, reportEnd]
        }
      ],
      order: {},
      filters: [
        {
          member: 'detection_case.clientUid',
          operator: 'equals',
          values: [clientUid]
        },
        {
          member: 'detection_case.priority',
          operator: 'notEquals',
          values: ['P5']
        },
        {
          member: 'detection_case.isIncident',
          operator: 'equals',
          values: ['0']
        },
        {
          member: 'detection_case.status',
          operator: 'equals',
          values: ['Closed']
        }
      ]
    }
  };

  const investigationsOpenQuery: VizState = {
    chartType: 'number',
    pivotConfig: {
      x: [],
      y: ['measures']
    },
    query: {
      measures: ['detection_case.count'],
      order: {},
      filters: [
        {
          member: 'detection_case.clientUid',
          operator: 'equals',
          values: [clientUid]
        },
        {
          member: 'detection_case.priority',
          operator: 'notEquals',
          values: ['P5']
        },
        {
          member: 'detection_case.isIncident',
          operator: 'equals',
          values: ['0']
        },
        {
          member: 'detection_case.status',
          operator: 'equals',
          values: ['Open']
        }
      ]
    }
  };

  const escalationsResolvedQuery: VizState = {
    chartType: 'number',
    pivotConfig: {
      x: [],
      y: ['measures']
    },
    query: {
      measures: ['detection_case.count'],
      timeDimensions: [
        {
          dimension: 'detection_case.closedDate',
          dateRange: [reportStart, reportEnd]
        }
      ],
      order: {},
      filters: [
        {
          member: 'detection_case.clientUid',
          operator: 'equals',
          values: [clientUid]
        },
        {
          member: 'detection_case.priority',
          operator: 'notEquals',
          values: ['P5']
        },
        {
          member: 'detection_case.isIncident',
          operator: 'equals',
          values: ['1']
        },
        {
          member: 'detection_case.status',
          operator: 'equals',
          values: ['Closed']
        }
      ]
    }
  };

  const escalationsOpenQuery: VizState = {
    chartType: 'number',
    pivotConfig: {
      x: [],
      y: ['measures']
    },
    query: {
      measures: ['detection_case.count'],
      order: {},
      filters: [
        {
          member: 'detection_case.clientUid',
          operator: 'equals',
          values: [clientUid]
        },
        {
          member: 'detection_case.priority',
          operator: 'notEquals',
          values: ['P5']
        },
        {
          member: 'detection_case.isIncident',
          operator: 'equals',
          values: ['1']
        },
        {
          member: 'detection_case.status',
          operator: 'equals',
          values: ['Open']
        }
      ]
    }
  };

  const { data: detectionCaseSLAData } = useGet<PieChartData>(
    `${securityEventApi}/report/detectioncases/sla`,
    {
      query: {
        clientUid: clientUid,
        startDate: reportStart,
        endDate: reportEnd
      }
    }
  );
  //end detection queries

  // Events Status
  const { resultSet: eventsByStatus } = useCubeQuery(eventsStatusQuery.query!);

  const eventsByStatusResults = useMemo(() => {
    if (eventsByStatus && eventsByStatus?.rawData())
      return {
        raw: eventsByStatus.rawData(),
        total: eventsByStatus
          .rawData()
          .reduce((acc, obj) => acc + parseInt(obj['security_event.count']), 0),
        new:
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'New') &&
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'New')[
            'security_event.count'
          ],
        linked:
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'Linked') &&
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'Linked')[
            'security_event.count'
          ],
        cleared:
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'Cleared') &&
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'Cleared')[
            'security_event.count'
          ],
        escalated:
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'Linked') &&
          eventsByStatus.rawData().find(item => item['security_event.status'] === 'Linked')[
            'security_event_extended.escalated_count'
          ]
      };
  }, [eventsByStatus]);

  //eventsByStatus && console.log(eventsByStatus.rawData());

  // Events SLA

  const { resultSet: eventsOverSLAResult } = useCubeQuery(eventsOverSLAQuery.query!);
  const eventsOverSLA =
    eventsOverSLAResult && eventsOverSLAResult.totalRow()[eventsOverSLAQuery.query!.measures![0]];
  const eventsUnderSLA = eventsByStatusResults && eventsByStatusResults.total - eventsOverSLA;
  const eventSLAPieData = [
    { type: 'Over SLA', value: Number(eventsOverSLA) },
    { type: 'Under SLA', value: Number(eventsUnderSLA) }
  ];
  //end events data

  //detections data
  const { resultSet: investigationsClosedData } = useCubeQuery(investigationsClosedQuery.query!);
  const investigationsClosed =
    investigationsClosedData &&
    investigationsClosedData.totalRow()[investigationsClosedQuery.query!.measures![0]];

  const { resultSet: investigationsOpenData } = useCubeQuery(investigationsOpenQuery.query!);
  const investigationsOpen =
    investigationsOpenData &&
    investigationsOpenData.totalRow()[investigationsOpenQuery.query!.measures![0]];

  const { resultSet: escalationsResolvedData } = useCubeQuery(escalationsResolvedQuery.query!);
  const escalationsResolved =
    escalationsResolvedData &&
    escalationsResolvedData.totalRow()[escalationsResolvedQuery.query!.measures![0]];

  const { resultSet: escalationsOpenData } = useCubeQuery(escalationsOpenQuery.query!);
  const escalationsOpen =
    escalationsOpenData && escalationsOpenData.totalRow()[escalationsOpenQuery.query!.measures![0]];

  const totalCases =
    investigationsClosed + investigationsOpen + escalationsResolved + escalationsOpen;

  const casesPieData: { type: string; value: number }[] = [];

  if (investigationsClosed > 0) {
    casesPieData.push({ type: 'Investigations Closed', value: Number(investigationsClosed) });
  }
  if (investigationsOpen > 0) {
    casesPieData.push({ type: 'Investigations Open', value: Number(investigationsOpen) });
  }
  if (escalationsResolved > 0) {
    casesPieData.push({ type: 'Escalations Resolved', value: Number(escalationsResolved) });
  }
  if (escalationsOpen > 0) {
    casesPieData.push({ type: 'Escalations Open', value: Number(escalationsOpen) });
  }

  const casesEscalatedToClient = escalationsResolved + escalationsOpen;

  const totalCasesEscalated =
    detectionCaseSLAData && detectionCaseSLAData.data && detectionCaseSLAData.data.length > 1
      ? detectionCaseSLAData.data[0].value + detectionCaseSLAData.data[1].value
      : null;
  const casesOverSLA =
    detectionCaseSLAData && detectionCaseSLAData.data
      ? detectionCaseSLAData.data.find(d => d.type === 'Over SLA')?.value
      : null;
  const casesUnderSLA =
    detectionCaseSLAData && detectionCaseSLAData.data
      ? detectionCaseSLAData.data.find(d => d.type === 'Under SLA')?.value
      : null;

  const [data, setData] = useState<any[]>([]);
  useEffect(() => {
    if (eventsByStatusResults && eventsByStatusResults.raw && eventsByStatusResults.raw != null) {
      const data = eventsByStatusResults.raw.map(itm => ({
        type: itm['security_event.status']
          .toString()
          .replace('New', 'Active')
          .replace('Linked', 'Promoted'),
        value: Number(itm['security_event.count'])
      }));
      setData(data);
    }
  }, [eventsByStatusResults]);

  return (
    <>
      <h2 style={{ marginTop: 0 }} className="text-uppercase">
        Percentages
      </h2>
      <Row>
        <Col span={10}>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              <h3 className="text-uppercase">Total Events:</h3>
            </Col>
            <Col>
              <h3>{eventsByStatusResults?.total}</h3>
            </Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Active:{' '}
            </Col>
            <Col>{eventsByStatusResults?.new}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Cleared:{' '}
            </Col>
            <Col>{eventsByStatusResults?.cleared}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Promoted:{' '}
            </Col>
            <Col>{eventsByStatusResults?.linked}</Col>
          </Row>
        </Col>
        <Col span={14}>
          <Pie
            theme={{ type: 'threatadvisor' }}
            angleField={'value'}
            colorField={'type'}
            data={data}
            legend={true}
            tooltip={false}
            label={{
              text: (d, i, data: []) => {
                const total = data.reduce(
                  (sum, current: { type; value }) => sum + current.value,
                  0
                );
                return `${d.type}\n${((d.value / total) * 100).toFixed(0)}%`;
              },
              style: {
                fontWeight: 'bold',
                fontSize: '10'
              }
            }}
          />
        </Col>
        <Col span={10}>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              <h3 className="text-uppercase">Total Events:</h3>
            </Col>
            <Col>
              <h3>{eventsByStatusResults?.total}</h3>
            </Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Time to Action Over SLA:{' '}
            </Col>
            <Col>{eventsOverSLA}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Time to Action Under SLA:{' '}
            </Col>
            <Col>{eventsUnderSLA}</Col>
          </Row>
        </Col>
        <Col span={14}>
          <Pie
            theme={{ type: 'threatadvisor' }}
            angleField={'value'}
            colorField={'type'}
            data={eventSLAPieData}
            legend={true}
            tooltip={false}
            label={{
              text: (d, i, data: []) => {
                const total = data.reduce(
                  (sum, current: { type; value }) => sum + current.value,
                  0
                );
                return `${d.type}\n${((d.value / total) * 100).toFixed(0)}%`;
              },
              style: {
                fontWeight: 'bold',
                fontSize: '10'
              }
            }}
          />
        </Col>
      </Row>
      <hr />
      <Row>
        <Col span={10}>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              <h3 className="text-uppercase">Total Cases:</h3>
            </Col>
            <Col>
              <h3>{totalCases}</h3>
            </Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Investigations Closed:{' '}
            </Col>
            <Col>{investigationsClosed}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Investigations Open:{' '}
            </Col>
            <Col>{investigationsOpen}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Escalations Resolved:{' '}
            </Col>
            <Col>{escalationsResolved}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Escalations Open:{' '}
            </Col>
            <Col>{escalationsOpen}</Col>
          </Row>
        </Col>
        <Col span={14}>
          <Pie
            theme={{ type: 'threatadvisor' }}
            angleField={'value'}
            colorField={'type'}
            data={casesPieData}
            legend={true}
            tooltip={false}
            label={{
              text: (d, i, data: []) => {
                const total = data.reduce(
                  (sum, current: { type; value }) => sum + current.value,
                  0
                );
                return `${((d.value / total) * 100).toFixed(0)}%`;
              },
              style: {
                fontWeight: 'bold',
                fontSize: '10'
              }
            }}
          />
        </Col>
        <Col span={10}>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              <h3 className="text-uppercase">Escalated Cases:</h3>
            </Col>
            <Col>
              <h3>{totalCasesEscalated}</h3>
            </Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Time to Escalation Over SLA:{' '}
            </Col>
            <Col>{casesOverSLA}</Col>
          </Row>
          <Row>
            <Col span={20} style={{ textAlign: 'right', paddingRight: 5 }}>
              Time to Escalation Under SLA:{' '}
            </Col>
            <Col>{casesUnderSLA}</Col>
          </Row>
        </Col>
        <Col span={14}>
          {detectionCaseSLAData && detectionCaseSLAData.data ? (
            <Pie
              theme={{ type: 'threatadvisor' }}
              angleField={'value'}
              colorField={'type'}
              data={detectionCaseSLAData.data}
              legend={true}
              tooltip={false}
              label={{
                text: (d, i, data: []) => {
                  const total = data.reduce(
                    (sum, current: { type; value }) => sum + current.value,
                    0
                  );
                  return `${((d.value / total) * 100).toFixed(0)}%`;
                },
                style: {
                  fontWeight: 'bold',
                  fontSize: '10'
                }
              }}
            />
          ) : null}
        </Col>
      </Row>

      <hr />

      <h3 className="text-uppercase">Event Reduction</h3>

      <p>{eventsByStatusResults?.total} Total Events Received</p>
      <p>{eventsByStatusResults?.linked} Events Promoted to Investigations</p>
      <p>{casesEscalatedToClient} Cases Escalated to Client</p>

      {eventsByStatusResults ? (
        <p>
          Client Received{' '}
          {((casesEscalatedToClient / eventsByStatusResults?.total) * 100).toFixed(0)}% of total
          alerts triggered across all cases escalated.
        </p>
      ) : null}
    </>
  );
}
